<template>
  <button :disabled="disabled" type="button" :class="classes" v-bind="$attrs">
    <TIcon v-if="props.icon" :icon="icon" :size="props.iconSize" />
    <slot>{{ text }} </slot>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { TIcon, TIconSize, type Icon } from '@tl/design-system';
import type {
  TButtonIconPosition,
  TButtonSupport,
  TButtonWidth,
} from '@/design-system/atoms/TButton/types';
import { TButtonSize, TButtonType } from '@/design-system/atoms/TButton/types';
import type { ValuesOf } from '@tl/design-system/dist/utils/helperTypes';

export interface Props {
  text?: string;
  disabled?: boolean;
  icon?: Icon;
  iconPosition?: TButtonIconPosition;
  type?: TButtonType;
  destructive?: boolean;
  width?: TButtonWidth;
  size?: TButtonSize;
  iconSize?: ValuesOf<typeof TIconSize>;
  support?: TButtonSupport;
}

defineOptions({
  name: 'TButton',
});

const props = withDefaults(defineProps<Props>(), {
  text: '',
  disabled: null,
  icon: null,
  iconPosition: null,
  type: null,
  destructive: false,
  width: null,
  size: TButtonSize.BIG,
  iconSize: TIconSize.MEDIUM,
  support: null,
});

const classes = computed(() => ({
  't-button': true,
  [`t-button--${props.type}`]: props.type && !props.destructive,
  [`t-button--${props.type}--destructive`]: props.type && props.destructive,
  [`t-button--${props.width}--width`]: props.width,
  [`t-button--${props.size}`]: props.size,
  [`t-button--${props.support}`]: props.support,
  [`t-button--tertiary--support`]: props.support && props.type === TButtonType.TERTIARY,
  [`t-button--icon--${props.iconPosition}`]: props.iconPosition,
}));
</script>
